import React, { useCallback, useMemo, useState } from "react";
import BubbleChartRoundedIcon from "@mui/icons-material/BubbleChartRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import SensorsRoundedIcon from "@mui/icons-material/SensorsRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import ViewModuleRoundedIcon from "@mui/icons-material/ViewModuleRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import { translateText } from "../../../../shared/utils/historyTranslation";
import { formatKpiDetailItems } from "../utils/summaryDashboardV3Utils";

export default React.memo(function SummaryV3Kpis({ stats, canSeeSensorFailures, translate }) {
  const t = useCallback((key, fallback) => translateText(translate, key, fallback), [translate]);
  const [activeKpi, setActiveKpi] = useState(null);
  const items = useMemo(() => {
    const nextItems = [
      {
        label: t("summary_v3_active_centers", "Centros activos"),
        value: stats.activeCenters,
        detail: t("summary_v3_in_monitoring", "en monitoreo"),
        tone: "info",
        Icon: HubRoundedIcon,
        rows: [
          [t("summary_v3_critical_plural", "Criticos"), stats.criticalCenters],
          [t("alertas", "Alertas"), stats.warningCenters],
          [t("summary_v3_legend_injecting", "Inyectando"), stats.injectingCenters],
          [t("summary_v3_normal_plural", "Normales"), stats.normalCenters],
          [t("no_data", "Sin datos"), stats.offlineCenters],
        ],
        items: stats.centerDetailItems,
      },
      {
        label: t("summary_v3_monitored_cages", "Jaulas monitoreadas"),
        value: stats.totalCages,
        detail: t("summary_v3_in_total", "en total"),
        tone: "info",
        Icon: ViewModuleRoundedIcon,
        rows: [
          [t("summary_v3_reliable_data", "Con dato confiable"), stats.reliableCages],
          [t("summary_v3_unreliable_data", "Sin dato confiable"), stats.unreliableCages],
          [t("manual", "Manual"), stats.manualCages],
        ],
        items: stats.cageDetailItems,
      },
      {
        label: t("summary_v3_below_min_threshold", "Bajo Umbral Min"),
        value: stats.belowThreshold,
        detail: `${stats.belowThresholdPercent}% ${t("summary_v3_of_total", "del total")}`,
        tone: "warn",
        Icon: WaterDropRoundedIcon,
        rows: [
          [t("summary_v3_legend_injecting", "Inyectando"), stats.belowInjecting],
          [t("summary_v3_auto_no_injection", "Auto sin inyeccion"), stats.belowAutoNoInjection],
          [t("manual", "Manual"), stats.belowManual],
        ],
        items: stats.belowThresholdItems,
      },
      {
        label: t("summary_v3_legend_injecting", "Inyectando"),
        value: stats.injecting,
        detail: `${stats.injectingPercent}% ${t("summary_v3_of_total", "del total")}`,
        tone: "ok",
        Icon: BubbleChartRoundedIcon,
        rows: [
          [t("summary_v3_below_min_threshold", "Bajo Umbral Min"), stats.belowInjecting],
          [t("summary_v3_above_min_threshold", "Sobre Umbral Min"), stats.injectingAboveThreshold],
        ],
        items: stats.injectingItems,
      },
      {
        label: t("summary_v3_below_min_without_injection", "Bajo Umbral Min sin inyeccion"),
        value: stats.belowWithoutInjection,
        detail: `${stats.belowWithoutInjectionPercent}% ${t("summary_v3_of_total", "del total")}`,
        tone: "danger",
        Icon: SpeedRoundedIcon,
        rows: [
          [t("summary_v3_auto_no_injection", "Auto sin inyeccion"), stats.belowAutoNoInjection],
          [t("manual", "Manual"), stats.belowManual],
        ],
        items: stats.belowWithoutInjectionItems,
      },
      {
        label: t("summary_v3_legend_problems", "Problemas"),
        value: stats.failures,
        detail: t("summary_v3_problem_detail", "generadores / comunicacion / presion"),
        tone: "danger",
        Icon: ErrorOutlineRoundedIcon,
        rows: [
          [t("summary_v3_generators", "Generadores"), stats.generatorFailures],
          [t("summary_v3_cages", "Jaulas"), stats.cageFailures],
          [t("sin_com", "Sin comunicacion"), stats.commFailures],
          [t("summary_v3_pressure_injection", "Presion / inyeccion"), stats.pressureInjectionFailures],
          [t("summary_v3_alarms", "Alarmas"), stats.alarmFailures],
        ],
        items: stats.failureItems,
      },
    ];

    if (canSeeSensorFailures) {
      nextItems.push({
        label: t("summary_v3_sensor_failures", "Fallas sensores"),
        value: stats.sensorFailures,
        detail: t("summary_v3_sensor_failures_detail", "O2 / temp / salinidad"),
        tone: "danger",
        Icon: SensorsRoundedIcon,
        rows: [
          ["O2", stats.sensorProblemCounts.o2],
          [t("temperatura", "Temperatura"), stats.sensorProblemCounts.temperature],
          [t("salinidad", "Salinidad"), stats.sensorProblemCounts.salinity],
        ],
        items: stats.sensorFailureItems,
      });
    }

    return nextItems;
  }, [stats, canSeeSensorFailures, t]);
  const handleActivateKpi = useCallback((label) => setActiveKpi(label), []);
  const handleDeactivateKpi = useCallback((label) => {
    setActiveKpi((current) => (current === label ? null : current));
  }, []);

  return (
    <div className="summary-v3__kpis" style={{ "--summary-v3-kpi-columns": items.length }}>
      {items.map((item) => (
        <SummaryV3KpiCard
          key={item.label}
          item={item}
          active={activeKpi === item.label}
          onActivate={handleActivateKpi}
          onDeactivate={handleDeactivateKpi}
          translate={translate}
        />
      ))}
    </div>
  );
});

const SummaryV3KpiCard = React.memo(function SummaryV3KpiCard({ item, active, onActivate, onDeactivate, translate }) {
  const Icon = item.Icon;
  const detailItems = useMemo(() => formatKpiDetailItems(item.items, 8, translate), [item.items, translate]);
  const handleActivate = useCallback(() => onActivate(item.label), [item.label, onActivate]);
  const handleDeactivate = useCallback(() => onDeactivate(item.label), [item.label, onDeactivate]);
  const handleBlur = useCallback((event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    handleDeactivate();
  }, [handleDeactivate]);

  return (
    <article
      className={`summary-v3__kpi summary-v3__kpi--${item.tone} ${active ? "is-active" : ""}`}
      tabIndex={0}
      onMouseEnter={handleActivate}
      onMouseLeave={handleDeactivate}
      onFocus={handleActivate}
      onBlur={handleBlur}
    >
      <span className="summary-v3__kpiIcon" aria-hidden="true">
        <Icon fontSize="inherit" />
      </span>
      <span>{item.label}</span>
      <strong>{item.value}</strong>
      <small>{item.detail}</small>
      <div className="summary-v3__kpiTooltip" role="tooltip">
        <b>{item.label}</b>
        {item.rows.map(([label, value]) => (
          <span key={`${item.label}-${label}`}>
            <em>{label}</em>
            <strong>{value}</strong>
          </span>
        ))}
        <div className="summary-v3__kpiTooltipDetail">
          <b>{translateText(translate, "summary_v3_detail", "Detalle")}</b>
          {/* ponytail: líneas de texto sin estado; si pasan a ser interactivas, conservar IDs desde el origen. */}
          {detailItems.map((detail, index) => (
            <em key={index}>{detail}</em>
          ))}
        </div>
      </div>
    </article>
  );
});
